import React from 'react';
import { Container, Header, SpaceBetween, Alert, Button, FormField, Input, Spinner, Grid, Box } from '@amzn/awsui-components-core';
import { EmployeeSearchBar } from '../components/attendees/EmployeeSearchBar';
import { AttendeeCard } from '../components/attendees/AttendeeCard';
import { MeetingFormData, ResolvedMatch, Employee } from '../interfaces/interface';
import { UploadTemplateContainer } from './UploadTemplateContainer';
import { CopyDropdown } from '../components/copy/CopyDropdown';
import './AddAttendeesContainer.scss';

interface AddAttendeesContainerProps {
  formData: MeetingFormData;
  onInputChange: (field: keyof MeetingFormData, value: string) => void;

  isProcessingDocument: boolean;
  resolvedAttendees: ResolvedMatch[];
  existingEmails: Set<string>;
  duplicateEmails: Set<string>;
  documentError: string;

  onAddAttendee: (employee: Employee, priority: 'required' | 'optional') => void;
  onAttendeeUpdate: (id: string, updates: Partial<ResolvedMatch>) => void;
  onAttendeeRemove: (id: string) => void;

  executiveAssistants: Array<{ name: string; email: string }>;

  onFileSelected: (file: File) => void;
  onFormattedContentExtracted: (htmlContent: string) => void;

  // Modal control props
  showUploadModal: boolean;
  onShowUploadModal: () => void;
  onHideUploadModal: () => void;

  // Multi Badger functionality
  onMultiBadgerClick: () => void;
  onDownloadCsv: () => void;
}

export const AddAttendeesContainer: React.FC<AddAttendeesContainerProps> = ({
  formData,
  onInputChange,
  isProcessingDocument,
  resolvedAttendees,
  existingEmails,
  duplicateEmails,
  onAddAttendee,
  onAttendeeUpdate,
  onAttendeeRemove,
  executiveAssistants,
  documentError,
  onFileSelected,
  onFormattedContentExtracted,
  showUploadModal,
  onShowUploadModal,
  onHideUploadModal,
  onMultiBadgerClick,
  onDownloadCsv
}) => {
  const requiredAttendees = resolvedAttendees.filter((a) => a.priority === 'required');
  const optionalAttendees = resolvedAttendees.filter((a) => a.priority === 'optional');

  return (
    <>
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <CopyDropdown
                  resolvedAttendees={resolvedAttendees}
                  executiveAssistants={executiveAssistants}
                  onMultiBadgerClick={onMultiBadgerClick}
                  onDownloadCsv={onDownloadCsv}
                />
                <Button variant="primary" onClick={onShowUploadModal}>
                  Upload Template
                </Button>
              </SpaceBetween>
            }
          >
            Review Attendee List
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="xs">
          {/* Responsive grid container for Subject and Add Attendees fields */}
          <div style={{ width: '100%' }}>
            <Grid
              gridDefinition={[
                { colspan: 5 }, // Add Attendees field - align with Attendees column
                { colspan: 5 }, // Subject field - align with Executive Assistants column
                { colspan: 1 }, // Empty space - align with Priority column
                { colspan: 1 } // Empty space - align with Actions column
              ]}
            >
              <FormField label="Add Attendees" description="Search for team members to add to your meeting">
                <EmployeeSearchBar onAddAttendee={onAddAttendee} existingEmails={existingEmails} />
              </FormField>
              <FormField
                label="Subject"
                description={
                  <span className="form-field-description-nowrap">
                    Meeting subject (can be entered manually or populated automatically from uploaded document)
                  </span>
                }
              >
                <Input
                  value={formData.subject}
                  onChange={({ detail }: { detail: { value: string } }) => onInputChange('subject', detail.value)}
                  placeholder="Enter meeting subject..."
                />
              </FormField>
              <Box /> {/* Empty space for Priority column alignment */}
              <Box /> {/* Empty space for Actions column alignment */}
            </Grid>
          </div>

          {/* Attendees Management Section - Show when attendees exist */}
          {resolvedAttendees.length > 0 && (
            <SpaceBetween direction="vertical" size="xs">
              {/* Attendees List Header */}
              <Box padding={{ vertical: 'xxs' }}>
                <div style={{ width: '100%' }}>
                  <Grid
                    gridDefinition={[
                      { colspan: 5 }, // Attendees info (equal width)
                      { colspan: 5 }, // Executive Assistants (equal width)
                      { colspan: 1 }, // Priority (compact)
                      { colspan: 1 } // Actions (compact)
                    ]}
                  >
                    <Box>
                      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                        <Box fontSize="body-m" fontWeight="bold" textAlign="left">
                          Attendees ({resolvedAttendees.length})
                          {resolvedAttendees.length > 0 && (
                            <Box display="inline" fontSize="body-s" color="text-body-secondary" fontWeight="normal" margin={{ left: 'xs' }}>
                              {' '}
                              ({requiredAttendees.length > 0 && `${requiredAttendees.length} required`}
                              {requiredAttendees.length > 0 && optionalAttendees.length > 0 && ', '}
                              {optionalAttendees.length > 0 && `${optionalAttendees.length} optional`})
                            </Box>
                          )}
                        </Box>
                      </SpaceBetween>
                    </Box>

                    <Box>
                      <Box fontSize="body-m" fontWeight="bold">
                        Executive Assistants ({executiveAssistants.length})
                      </Box>
                    </Box>
                  </Grid>
                </div>
              </Box>

              {/* Show loading state during document processing or attendees list */}
              {isProcessingDocument ? (
                <Alert statusIconAriaLabel="Loading" type="info" header="Document Processing">
                  <SpaceBetween direction="horizontal" size="s" alignItems="center">
                    <Spinner size="normal" />
                    <span>Processing document and resolving attendees...</span>
                  </SpaceBetween>
                </Alert>
              ) : (
                <>
                  {/* Duplicate Warning */}
                  {duplicateEmails.size > 0 && (
                    <Alert statusIconAriaLabel="Warning" type="warning" header="Duplicate Email Alert">
                      Some attendees have duplicate email addresses. They will only be invited once.
                    </Alert>
                  )}

                  {/* Attendees List */}
                  <div className="attendees-list">
                    {resolvedAttendees.map((attendee) => (
                      <AttendeeCard
                        key={attendee.id}
                        attendee={attendee}
                        onUpdate={onAttendeeUpdate}
                        onRemove={onAttendeeRemove}
                        isDuplicate={attendee.resolvedEmail ? duplicateEmails.has(attendee.resolvedEmail.toLowerCase()) : false}
                        existingEmails={existingEmails}
                      />
                    ))}
                  </div>
                </>
              )}
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Container>

      {/* Upload Template Modal */}
      {showUploadModal && (
        <UploadTemplateContainer
          visible={showUploadModal}
          onClose={onHideUploadModal}
          isProcessingDocument={isProcessingDocument}
          documentError={documentError}
          onFileSelected={onFileSelected}
          onFormattedContentExtracted={onFormattedContentExtracted}
        />
      )}
    </>
  );
};
